import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Divider,
    Chip,
} from "@mui/material";
import { columns, Get_URI, Get_Customer_URI } from "./config";
import CustomDataGrid from "../../Components/CustomDataGrid";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useNavigate } from "react-router-dom";
import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useReactToPrint } from "react-to-print";
import { useLang } from "../../context/LanguageContext";
import { formatDate } from "../../Utils/dateFormatter";

const UnpaidInvoicesReport = () => {
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [totalUnpaidAmount, setTotalUnpaidAmount] = useState(0);
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    const printComponentRef = useRef();
    const { t } = useLang();

    // Setup the print functionality using the hook
    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current,
        documentTitle: "Unpaid Invoices Report",
        pageStyle:
            "@page { size: auto; margin: 10mm; } @media print { body { margin: 0; padding: 20px; } }",
        onAfterPrint: () => toast.success("Report printed successfully!"),
    });

    // Actions for the data grid
    const actions = [
        {
            title: "View Invoice",
            color: "info",
            handler: (id) => {
                navigate("/Invoice/" + id);
            },
            parameters: ["id"],
            icon: <RemoveRedEye color="info" />,
        },
    ];

    // Get all customers
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await fetch(
                    `${REACT_APP_BACKEND_URL}/otherparty/customers`,
                    {
                        headers: {
                            Authorization: authHeader,
                        },
                    },
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch customers");
                }

                const data = await response.json();
                if (data.success) {
                    setCustomers(data.data);
                } else {
                    toast.error("Could not load customers");
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
                toast.error("Error fetching customers");
            }
        };

        fetchCustomers();
    }, [authHeader]);

    // Fetch unpaid invoices
    useEffect(() => {
        const fetchUnpaidInvoices = async () => {
            setIsLoading(true);
            try {
                const url = selectedCustomerId
                    ? `${REACT_APP_BACKEND_URL}${Get_Customer_URI(
                          selectedCustomerId,
                      )}`
                    : `${REACT_APP_BACKEND_URL}${Get_URI}`;

                const response = await fetch(url, {
                    headers: {
                        Authorization: authHeader,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch unpaid invoices");
                }

                const data = await response.json();
                if (data.success) {
                    setUnpaidInvoices(data.data);

                    // Calculate total unpaid amount
                    const total = data.data.reduce(
                        (sum, invoice) => sum + Number(invoice.remainingAmount),
                        0,
                    );
                    setTotalUnpaidAmount(total);
                } else {
                    toast.error("Could not load unpaid invoices");
                }
            } catch (error) {
                console.error("Error fetching unpaid invoices:", error);
                toast.error("Error fetching unpaid invoices");
            } finally {
                setIsLoading(false);
            }
        };

        fetchUnpaidInvoices();
    }, [authHeader, selectedCustomerId]);

    // Handle customer selection change
    const handleCustomerChange = (event) => {
        setSelectedCustomerId(event.target.value);
    };

    // Reset customer selection
    const handleResetFilter = () => {
        setSelectedCustomerId("");
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #e53935 0%, #b71c1c 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ReceiptIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("UNPAID_INVOICES")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip
                                label={`${unpaidInvoices.length} ${t("INVOICE")}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                            <Chip
                                label={`${t("REMAINING_AMOUNT")}: ${totalUnpaidAmount.toFixed(2)}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        border: "1.5px solid rgba(255,255,255,0.4)",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                    }}
                >
                    {t("PRINT")}
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                            <InputLabel id="customer-select-label">
                                {t("FILTER_BY_CUSTOMER")}
                            </InputLabel>
                            <Select
                                labelId="customer-select-label"
                                id="customer-select"
                                value={selectedCustomerId}
                                label={t("FILTER_BY_CUSTOMER")}
                                onChange={handleCustomerChange}
                            >
                                {customers.map((customer) => (
                                    <MenuItem
                                        key={customer.id}
                                        value={customer.id}
                                    >
                                        {customer.name} - {customer.phone}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleResetFilter}
                            fullWidth
                        >
                            {t("RESET_FILTER")}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <div ref={printComponentRef}>
                <Box
                    sx={{
                        mb: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h5" component="div" gutterBottom>
                        {selectedCustomerId
                            ? `Unpaid Invoices for ${
                                  customers.find(
                                      (c) =>
                                          c.id === Number(selectedCustomerId),
                                  )?.name || "Selected Customer"
                              }`
                            : "All Unpaid Invoices"}
                    </Typography>
                    <Box sx={{ textAlign: "right" }}>
                        <Typography variant="subtitle1" component="div">
                            Report Date: {formatDate(new Date())}
                        </Typography>
                    </Box>
                </Box>

                <CustomDataGrid
                    rows={unpaidInvoices}
                    columns={columns}
                    actions={actions}
                    loading={isLoading}
                    getRowClassName={(params) =>
                        params.row.remainingAmount > 0 ? "unpaid-row" : ""
                    }
                />

                <Divider sx={{ my: 2 }} />

                <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                >
                    <Paper sx={{ p: 2, width: "300px" }}>
                        <Typography variant="h6" gutterBottom>
                            Summary
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                            }}
                        >
                            <Typography>Total Invoices:</Typography>
                            <Typography fontWeight="bold">
                                {unpaidInvoices.length}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography>Total Unpaid Amount:</Typography>
                            <Typography fontWeight="bold">
                                {totalUnpaidAmount.toFixed(4)}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </div>
        </Box>
    );
};

export default UnpaidInvoicesReport;
